import { useSession } from '@/providers/session-provider';
import { Urls } from '@/lib/urls';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  failedInvoicesCountQueryOptions,
  useCanViewFailedInvoices,
} from '@/app/dashboard/invoicing/failed-deliveries/utils';

type CountResponse = { count: number; identifier?: string };

type CountQueryFactory = (args: {
  organizationId?: string;
  enabled?: boolean;
  canViewFailedInvoices: boolean;
}) => ReturnType<typeof failedInvoicesCountQueryOptions>;

const customCountQueryRegistry: Record<string, CountQueryFactory> = {
  [Urls.dashboard.invoicing.failedDelivieries]: ({
    organizationId,
    canViewFailedInvoices,
  }) =>
    failedInvoicesCountQueryOptions({
      organizationId,
      countOnly: true,
      enabled: canViewFailedInvoices,
    }),
};

export function useCustomCountByPath(
  path: string
):
  | (Omit<UseQueryResult<CountResponse, Error>, 'data'> & {
      data: CountResponse | undefined;
    })
  | undefined {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const canViewFailedInvoices = useCanViewFailedInvoices();

  const factory = customCountQueryRegistry[path];

  const queryOptions = factory
    ? factory({ organizationId, canViewFailedInvoices })
    : {
        queryKey: ['empty', path],
        queryFn: async () => ({ count: 0 }),
        enabled: false,
      };

  const result = useQuery(queryOptions);
  const isEmptyQuery = queryOptions.queryKey[0] === 'empty';

  return isEmptyQuery
    ? undefined
    : {
        ...result,
        data:
          canViewFailedInvoices && !result.isError ? result.data : undefined,
        isLoading: canViewFailedInvoices && result.isLoading,
      };
}
